import type { XComponentsAdapter } from '@empathyco/x-types'
import type { Container } from 'inversify'
import type { Component } from 'vue'
import type { Store } from 'vuex'
import type { AppConfig, FeaturesConfig, ServicesConfig } from '../../models'
import Vue from 'vue'
import Router from 'vue-router'
import { TYPES } from '../../config/inversify.types'
import { OIDC_CONSTS } from '../../store/oidc/oidc.consts'
import { OIDC_MODULE_METHOD, vuexOidcCreateRouterMiddleware } from '../../store/oidc/oidc.module'
import { useX } from '../../x'
import { initApp } from './init-app'
import { initServices } from './init-services'

export interface InitParams {
  config: {
    app: AppConfig
    services: ServicesConfig
    features: FeaturesConfig
    x: {
      endpoints: {
        search: string
      }
      adapter?: Partial<XComponentsAdapter>
    }
  }
  container: Container
  store: Store<unknown>
  router: Router
  messages: { [locale: string]: { [module: string]: unknown } }
  rootComponent: Component
}

export function init({
  config,
  container,
  store,
  router,
  messages,
  rootComponent,
}: InitParams): Vue {
  container.rebind<typeof config.features>(TYPES.FeaturesConfig).toConstantValue(config.features)
  container.rebind<typeof store>(TYPES.Store).toConstantValue(store)
  container.rebind<typeof messages>(TYPES.Messages).toConstantValue(messages)

  initApp(config.app)

  initServices(config.services)

  if (config.app.oidcLogin) {
    /**
     * IMPORTANT: OIDC module method must be called after `initServices(...)` as it uses
     * `TYPES.ServicesConfig` internally
     */
    store.registerModule(OIDC_CONSTS.moduleName, OIDC_MODULE_METHOD())
    router.beforeEach(vuexOidcCreateRouterMiddleware(store, OIDC_CONSTS.moduleName))
  }

  useX({
    store,
    ...config.x,
  })

  Vue.use(Router)
  return new Vue({
    store,
    router,
    render: h => h(rootComponent),
  })
}

export { initApp, initServices }
